import { TrackingEventTypes } from '@eagle/data-function-types';
import { Button, Stack, Switch, Typography } from '@ethings/ui-toolkit';
import { ScaleControl, ZoomControls } from '@ethings/ui-toolkit/map';
import { useTheme } from '@mui/material';
import L from 'leaflet';
import { useSnackbar } from 'notistack';
import { Dispatch, FC, PropsWithChildren, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer } from 'react-leaflet';
import { EventLocationData, FlexBox, LoadingComponent, MapLayerController, MapLayersProvider, SavePosition, SyncZoomLimits, ThingEventPane, useBoolFlag, useHistorySearch, ZOOM } from '../../components';
import { useNotableEventsContext } from '../../components/entity-journey';
import { BOUNDS_OF_EARTH, HISTORY_MAP_CENTER } from '../../constants';
import { MapProvider, useMapZoomLimits, useResponsiveText, useSavedPosition, useSmallScreen } from '../../hooks';
import { isNotableEvent } from '../../notable-events';
import { Maybe } from '../../types/common';
import { MapStorageKeys } from '../../types/map';
import { innerText, savedPositionCheck, useLocalStorage, ValidateResize } from '../../util';

interface Props extends PropsWithChildren {
  center: L.LatLng;
  hoveredEventId?: Maybe<string>;
  onSelectEvent?: (event: EventLocationData) => void;
  selectedEvent: Maybe<EventLocationData>;
  setHoveredEventId: Dispatch<SetStateAction<Maybe<string>>>;
  setSelectedEvent: Dispatch<SetStateAction<Maybe<EventLocationData>>>;
  storageKey: MapStorageKeys;
  visibleJourneyIds?: string[];
  visibleTripId?: string;
  zoom?: number;
}

export const EventHistoryMap: FC<Props> = ({
  center,
  children,
  hoveredEventId,
  onSelectEvent,
  selectedEvent,
  setHoveredEventId,
  setSelectedEvent,
  storageKey,
  visibleJourneyIds,
  visibleTripId,
  zoom = ZOOM.earth,
}) => {
  const { t } = useTranslation(['common', 'track']);
  const { enqueueSnackbar } = useSnackbar();
  const flagSnapToRoad = useBoolFlag('track-history-map-snap-to-road-toggle');
  const flagRouteAnimationSwitch = useBoolFlag('thing-history-page-route-animation-switch');
  const theme = useTheme();
  const { minZoom, maxZoom } = useMapZoomLimits();
  const routeAnimationStorageKey = MapStorageKeys.HISTORY_ROUTE_ANIMATION_PREFERENCE as string;
  const [isAntPolylineEnabled, setIsAntPolylineEnabled] = useLocalStorage(
    routeAnimationStorageKey,
    !flagRouteAnimationSwitch,
  );
  const { savedPosition } = useSavedPosition(storageKey);
  const smallScreen = useSmallScreen();
  const {
    eventData,
    eventLimit,
    mapLoading,
    setSnapToRoad,
    snapToRoad,
  } = useHistorySearch();
  const { notableEvents } = useNotableEventsContext();
  const savedCenter = savedPosition && new L.LatLng(savedPosition.lat, savedPosition.lng);
  const visibleEventData = useMemo(() => {
    return eventData
      .filter((item) => {
        if (visibleJourneyIds && !visibleJourneyIds.includes(item.journeyId)) return false;
        if (visibleTripId && item.tripId !== visibleTripId) return false;
        return true;
      })
      .map((item) => {
        const filteredItems = item.data.items.filter((eventItem) => isNotableEvent(eventItem, notableEvents));
        return {
          ...item,
          data: {
            ...item.data,
            count: filteredItems.length,
            items: filteredItems,
          },
        };
      });
  }, [eventData, notableEvents, visibleJourneyIds, visibleTripId]);
  const noEvents = visibleEventData.every((item) => !item.data.count);
  const mapLoadingCheck = Object.values(mapLoading).some((value) => value === true);

  const snapToRoadText = t('common:component.map.labels.snap-to-road');
  const routeAnimationText = t('common:component.map.labels.route-animation');

  const snapToRoadStyle = useResponsiveText('0.875rem', innerText(snapToRoadText).length, {
    minFontSize: '0.75rem',
    maxFontSize: '0.875rem',
    textLengthThreshold: 20,
  });

  const routeAnimationStyle = useResponsiveText('0.875rem', innerText(routeAnimationText).length, {
    minFontSize: '0.75rem',
    maxFontSize: '0.875rem',
    textLengthThreshold: 20,
  });

  useEffect(() => {
    savedPositionCheck(storageKey);
  }, [storageKey]);

  const onClick = (): void => {
    if (mapLoadingCheck) return;
    if (!snapToRoad) {
      const eventsOutsideRange = visibleEventData.filter(({ data: { items } }) =>
        items.filter(({ eventTypeId }) => eventTypeId === TrackingEventTypes.LOCATION_UPDATE).length > eventLimit,
      );
      if (eventsOutsideRange.length !== visibleEventData.length) setSnapToRoad(!snapToRoad);
      eventsOutsideRange.forEach(({ entityDisplay }) =>
        enqueueSnackbar(t('track:page.history.too-many-locations.hint.failure', { display: entityDisplay }), { variant: 'info' }),
      );
      return;
    }
    setSnapToRoad(!snapToRoad);
  };

  return (
    <MapProvider>
      <LoadingComponent isLoading={mapLoadingCheck} />
      <FlexBox
        sx={{
          'svg > g > path': { outline: 'none !important' },
          '& .leaflet-container': {
            filter: noEvents ? 'blur(4px)' : 'none',
            height: '100%',
            pointerEvents: noEvents ? 'none' : 'auto',
            width: '100%',
          },
          '& .leaflet-div-icon': { alignItems: 'center', background: 'none', border: 'none', display: 'flex' },
          height: '100%',
          width: '100%',
          minWidth: 0,
          position: 'relative',
          '& .leaflet-popup-content': { margin: 0 },
          '& .leaflet-popup-content-wrapper': { padding: 0 },
        }}
      >
        <MapLayersProvider>
          <MapContainer
            center={savedCenter ?? center}
            maxBounds={BOUNDS_OF_EARTH}
            maxBoundsViscosity={1}
            maxZoom={maxZoom}
            minZoom={minZoom}
            preferCanvas={true}
            renderer={new L.Canvas()}
            touchZoom={true}
            worldCopyJump={false}
            zoom={savedPosition?.alt ?? zoom}
            zoomAnimation={true}
            zoomAnimationThreshold={2}
            zoomControl={false}
            zoomDelta={1}
            zoomSnap={0.5}
          >
            <SyncZoomLimits />
            <MapLayerController drawerLayerSelection={smallScreen} shouldShowLayerSelection={!noEvents} />
            {children}
            {!noEvents &&
              <Stack>
                <Stack direction="row" spacing={2} position="inherit" m={2} zIndex={400} justifyContent={smallScreen ? 'center' : 'inherit'}>
                  {flagSnapToRoad &&
                    <Button
                      color="primary"
                      data-testid="snap-to-road-button"
                      disabled={mapLoadingCheck}
                      onClick={(event) => {
                        event.stopPropagation();
                        onClick();
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                      variant='light'
                    >
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <Switch
                          size="small"
                          checked={snapToRoad}
                          data-testid="snap-to-road-switch"
                          disabled={mapLoadingCheck}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => onClick()}
                        />
                        <Typography style={snapToRoadStyle}>{snapToRoadText}</Typography>
                      </Stack>
                    </Button>
                  }
                  {flagRouteAnimationSwitch &&
                    <Button
                      color="primary"
                      data-testid="route-animation-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setIsAntPolylineEnabled((value) => !value);
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                      variant='light'
                    >
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <Switch
                          size="small"
                          checked={isAntPolylineEnabled}
                          data-testid="route-animation-switch"
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => setIsAntPolylineEnabled((value) => !value)}
                        />
                        <Typography style={routeAnimationStyle}>{routeAnimationText}</Typography>
                      </Stack>
                    </Button>
                  }
                </Stack>
                <ValidateResize />
                <ZoomControls bottom={3} right={2} />
                <ScaleControl imperial />
              </Stack>
            }
            <ThingEventPane
              hoveredEventId={hoveredEventId}
              isAntPolylineEnabled={isAntPolylineEnabled}
              onSelectEvent={onSelectEvent}
              selectedEvent={selectedEvent}
              setHoveredEventId={setHoveredEventId}
              setSelectedEvent={setSelectedEvent}
              storageKey={MapStorageKeys.HISTORY_JOURNEY_SAVED_POSITION}
              visibleJourneyIds={visibleJourneyIds}
              visibleTripId={visibleTripId}
            />
            <SavePosition initialCenter={HISTORY_MAP_CENTER} storageKey={storageKey} />
          </MapContainer>
          {noEvents &&
            <FlexBox
              sx={{
                alignItems: 'center',
                bottom: 0,
                justifyContent: 'center',
                left: 0,
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            >
              <Typography
                color="text.secondary"
                fontStyle="italic"
                style={{ textShadow: `0 0 ${theme.spacing(1)} ${theme.palette.background.paper}, 0 0 ${theme.spacing(2)} ${theme.palette.background.paper}, 0 0 ${theme.spacing(3)} ${theme.palette.background.paper}` }}
                variant="body2"
              >
                {t('common:component.map.hint.no-location-information')}
              </Typography>
            </FlexBox>
          }
        </MapLayersProvider>
      </FlexBox >
    </MapProvider >
  );
};
